<template>
  <article
    class="kds-feature-card"
    :class="{ 'kds-feature-card--glass': variant === 'glass' }"
  >
    <KdsIcon
      class="kds-feature-card__icon"
      :name="icon"
      :size="20"
      aria-hidden="true"
    />
    <h3 class="kds-feature-card__title">{{ title }}</h3>
    <p class="kds-feature-card__body">{{ body }}</p>
    <router-link
      v-if="linkTo && linkLabel"
      :to="linkTo"
      class="kds-feature-card__link"
    >
      {{ linkLabel }}
      <KdsIcon name="arrow-right" :size="16" aria-hidden="true" />
    </router-link>
  </article>
</template>

<script setup lang="ts">
import KdsIcon from '@/kds/primitives/KdsIcon.vue';
import type { KdsIconName } from '@/kds/types';

type Props = {
  icon: KdsIconName;
  title: string;
  body: string;
  linkLabel?: string;
  linkTo?: string;
  /** Visual variant: 'default' uses --paper background; 'glass' applies the glassmorphism mixin (D2, CA-4). */
  variant?: 'default' | 'glass';
};

withDefaults(defineProps<Props>(), {
  linkLabel: undefined,
  linkTo: undefined,
  variant: 'default'
});
</script>

<style scoped lang="scss">
@use '@/css/kds-glass' as glass;

.kds-feature-card {
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
  padding: var(--space-5);
  background: var(--paper);
  border: var(--border);
  border-radius: var(--r-4);
  box-shadow: var(--shadow-1);
  transition:
    box-shadow var(--d-base) var(--ease-out),
    border-color var(--d-base) var(--ease-out);

  &--glass {
    @include glass.kds-glass-surface;

    // D1(a): softer hover shadow on glass cards — avoid heavy elevation over transparent surface.
    &:hover {
      box-shadow: var(--shadow-1);
    }
  }

  &:hover {
    box-shadow: var(--shadow-2);
    border-color: var(--mist-2);
  }

  &__icon {
    color: var(--stone);
    flex-shrink: 0;
  }

  &__title {
    margin: 0;
    font-family: var(--font-ui);
    font-size: var(--fs-h3);
    font-weight: var(--fw-semibold);
    line-height: var(--lh-h3);
    color: var(--ink);
  }

  &__body {
    margin: 0;
    font-size: var(--fs-body-sm);
    line-height: 1.5;
    color: var(--stone);
    flex: 1;
  }

  &__link {
    display: inline-flex;
    align-items: center;
    gap: var(--space-1);
    font-size: var(--fs-body-sm);
    font-weight: var(--fw-medium);
    color: var(--ink);
    text-decoration: none;
    transition: color var(--d-fast) var(--ease-out);
    margin-top: auto;

    &:hover {
      text-decoration: underline;
    }

    &:focus-visible {
      outline: none;
      box-shadow: var(--shadow-focus);
      border-radius: var(--r-2);
    }
  }
}
</style>
